import { Primitive } from "type-fest";

export const pathParamSanitizer = (
  path: string,
  params: GenericObj<Primitive> | undefined,
  identifier: "{}" | ":" = ":"
) => {
  return Object.entries(params || {}).reduce(
    (acc, [key, value]) =>
      acc.replace(identifier === "{}" ? `{${key}}` : `:${key}`, String(value)),
    path
  );
};

export const pathQuerySanitizer = (path: string, query?: any) => {
  if (!query) return path;
  const urlSearchParams = new URLSearchParams();

  Object.entries(query).forEach((entry) => {
    urlSearchParams.set(entry[0], String(entry[1]));
  });

  let sanitizedPath = urlSearchParams.toString();

  sanitizedPath = `${path}${path.includes("?") ? "" : "?"}${sanitizedPath}`;

  return sanitizedPath;
};

export function atob(base64: string) {
  try {
    return window.atob(base64);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err, "Error while atob");
  }
}

export function btoa(data: string) {
  try {
    return window.btoa(data);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err, "Error while btoa");
  }
}

export function urlSafeBtoa(input: string): string {
  try {
    const base64 = btoa(input) as string;
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  } catch (error) {
    console.error("Error during base64 encoding:", error);
    return "";
  }
}

export function urlSafeAtob(encodedString: string) {
  try {
    const paddingChar = "=";
    const safeBase64 = encodedString
      .replace(/-/g, "+")
      .replace(/_/g, "")
      .replace(/[^A-Za-z0-9+/]/g, "");

    const paddingLength = 4 - (safeBase64.length % 4);
    const paddedBase64 = safeBase64 + paddingChar.repeat(paddingLength % 4);

    return atob(paddedBase64);
  } catch (error) {
    console.error("Error during base64 decoding:", error);
    return "";
  }
}

export function maskString(input = "", characterToShow = 4, maskChar = "•") {
  const maskedLength = Math.max(input.trim().length - characterToShow, 0);
  return maskChar.repeat(maskedLength) + input.slice(maskedLength);
}

export const maskPhoneNumberOrEmail = (input: string) => {
  // Check if the input looks like a phone number
  if (/^\d{10}$/.test(input)) {
    // If it's a 10-digit number, mask it like a phone number
    return input.replace(/^(\d{3})(\d{3})(\d{2})(\d{2})$/, "$1*****$4");
  }

  // Check if the input looks like an email address
  if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input)) {
    // If it's an email address, mask it like an email
    const [username, domain] = input.split("@");
    const maskedUsername = `${username.charAt(0)}${"*".repeat(
      username.length - 2
    )}${username.charAt(username.length - 1)}`;
    return `${maskedUsername}@${domain}`;
  }

  // If the input doesn't match either pattern, return it as is
  return input;
};

// eslint-disable-next-line
export const safeParseJson = <T = any>(data: string | undefined) => {
  if (!data) return;
  try {
    return JSON.parse(data) as T;
  } catch (err) {
    console.error(`safeParseJson - Error`, err);
  }
};

export const commaSeparateNum = (
  num: string | number,
  format: "en" | "np" = "np"
) => {
  const numAndDecimal = String(num).split(".");
  let updatedNum = numAndDecimal[0];
  const numArr: Array<string> = [];

  while (updatedNum.length) {
    // eslint-disable-next-line no-nested-ternary

    const numArrLen = numArr.length ? 2 : 3;
    const lengthToSplit = format === "en" ? 3 : numArrLen;
    numArr.push(
      updatedNum.substring(updatedNum.length - lengthToSplit, updatedNum.length)
    );
    updatedNum = updatedNum.substring(0, updatedNum.length - lengthToSplit);
  }

  const formattedNum = numArr.slice().reverse().join(",");
  const decimalPart = Number(`0.${numAndDecimal[1]}`).toFixed(3).split(".")[1];
  const numDecimal = `.${decimalPart}`;
  return `${formattedNum}${numAndDecimal[1] && numDecimal}`;
};
